'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { toast } from 'sonner';
import { subscribeQueue, replayQueue, queueSize, type ReplayResult } from '@/lib/offlineQueue';
interface OfflineIndicatorProps {
  /**
   * Inject a replay function for tests. Defaults to the real replayQueue.
   */
  replay?: () => Promise<ReplayResult>;
}
function readOnline(): boolean {
  return navigator.onLine !== false;
}
export function OfflineIndicator(t0) {
  const $ = _c(16);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    replay
  } = t1;
  const [online, setOnline] = useState(_temp);
  const [pending, setPending] = useState(0);
  let t2;
  let t3;
  if ($[2] !== replay) {
    t2 = () => {
      const handleOnline = () => {
        setOnline(true);
        const run = replay ?? _temp2;
        run().then(_temp3).catch(_temp4);
      };
      const handleOffline = () => setOnline(false);
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      const unsubscribe = subscribeQueue(size => setPending(size));
      queueSize().then(n => setPending(n));
      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
        unsubscribe();
      };
    };
    t3 = [replay];
    $[2] = replay;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const showOffline = !online;
  const showBadge = pending > 0;
  if (!showOffline && !showBadge) {
    return null;
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      vertical: "bottom",
      horizontal: "left"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = showOffline ? "warning" : "info";
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CloudOffIcon fontSize="inherit" />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = !showBadge;
  const t8 = showOffline ? "You are offline \u2014 changes will sync when you reconnect" : `${pending} pending sync${pending === 1 ? "" : "s"}`;
  let t9;
  if ($[7] !== t8) {
    t9 = <span>{t8}</span>;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== pending || $[10] !== t7 || $[11] !== t9) {
    t10 = <Badge badgeContent={pending} color="error" invisible={t7} data-testid="offline-pending-badge">{t9}</Badge>;
    $[9] = pending;
    $[10] = t7;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t5) {
    t11 = <Snackbar open={true} anchorOrigin={t4} data-testid="offline-indicator"><Alert severity={t5} icon={t6} variant="filled">{t10}</Alert></Snackbar>;
    $[13] = t10;
    $[14] = t5;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  return t11;
}
function _temp4() {
  toast.error("Failed to replay offline changes");
}
function _temp3(res) {
  if (res.attempted === 0) {
    return;
  }
  const pending_0 = (res.failed ?? 0) - (res.dropped ?? 0);
  if (res.failed === 0) {
    toast.success(`Synced ${res.succeeded} pending change${res.succeeded === 1 ? "" : "s"}`);
  } else {
    if (res.succeeded === 0 && pending_0 === 0) {
      toast.error(`Gave up on ${res.dropped} change${res.dropped === 1 ? "" : "s"} (server rejected)`);
    } else {
      if (res.succeeded === 0) {
        toast.error(`Failed to sync ${res.failed} change${res.failed === 1 ? "" : "s"}`);
      } else {
        if (pending_0 === 0) {
          toast.error(`Synced ${res.succeeded}; gave up on ${res.dropped} (server rejected)`);
        } else {
          toast.error(`Synced ${res.succeeded}; ${pending_0} still pending`);
        }
      }
    }
  }
}
function _temp2() {
  return replayQueue();
}
function _temp() {
  return readOnline();
}